import React from 'react';
import {
    Box,
    Text,
    TextInput,
    ActionIcon,
    Select,
    Group,
    Loader,
} from '@mantine/core';
import { Button } from '@xplatform/design-system';
import { IconTrash } from '@tabler/icons-react';
import type { UserInstructionViewModel } from '@models/extraction';

const NEW_INSTRUCTION_VALUE = '__new__';
const PORTAL_TARGET = '#extraction-portal-root';

interface InstructionSelectorProps {
    savedInstructions: UserInstructionViewModel[];
    isLoading: boolean;
    isSaving: boolean;
    selectedId: string | null;
    instructionName: string;
    nameError: string;
    instructionText: string;
    onSelectChange: (value: string | null) => void;
    onNameChange: (value: string) => void;
    onSave: () => void;
    onDelete: (id: string, e: React.MouseEvent) => void;
    isNewInstruction: boolean;
    isExistingInstruction: boolean;
    isInstructionDirty: boolean;
    /** Render only the Select dropdown (for inline use in title row) */
    inline?: boolean;
    /** Render only the name input + save/update row */
    nameOnly?: boolean;
}

const InstructionSelector: React.FC<InstructionSelectorProps> = ({
    savedInstructions,
    isLoading,
    isSaving,
    selectedId,
    instructionName,
    nameError,
    instructionText,
    onSelectChange,
    onNameChange,
    onSave,
    onDelete,
    isNewInstruction,
    isExistingInstruction,
    isInstructionDirty,
    inline,
    nameOnly,
}) => {
    const selectData = [
        { value: NEW_INSTRUCTION_VALUE, label: '+ New instruction' },
        ...savedInstructions.map((i) => ({ value: i.id, label: i.name })),
    ];

    const saveButtonDisabled = isSaving
        || !instructionText.trim()
        || !instructionName.trim()
        || (isExistingInstruction && !isInstructionDirty);

    // Inline mode: render only the Select dropdown
    if (inline) {
        return (
            <Select
                size="xs"
                placeholder={isLoading ? 'Loading…' : 'Select or create'}
                data={selectData}
                value={selectedId}
                onChange={onSelectChange}
                disabled={isLoading}
                rightSection={isLoading ? <Loader size="xs" /> : undefined}
                clearable
                style={{ minWidth: 180, maxWidth: 220 }}
                comboboxProps={{
                    portalProps: { target: PORTAL_TARGET },
                    zIndex: 1001,
                }}
                renderOption={({ option }) => (
                    <Group
                        justify="space-between"
                        style={{ width: '100%' }}
                        wrap="nowrap"
                    >
                        <Text
                            size="xs"
                            truncate="end"
                            style={{ flex: 1 }}
                            c={option.value === NEW_INSTRUCTION_VALUE ? 'brand.6' : undefined}
                            fw={option.value === NEW_INSTRUCTION_VALUE ? 600 : undefined}
                        >
                            {option.label}
                        </Text>
                        {option.value !== NEW_INSTRUCTION_VALUE && (
                            <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="red"
                                aria-label={`Delete ${option.label}`}
                                onClick={(e) => onDelete(option.value, e)}
                            >
                                <IconTrash size={12} />
                            </ActionIcon>
                        )}
                    </Group>
                )}
            />
        );
    }

    if (nameOnly) {
        return (
            <Box mb="xs">
                <Group gap="xs" align="flex-start">
                    <TextInput
                        size="xs"
                        placeholder="Instruction name (max 50 chars)"
                        value={instructionName}
                        onChange={(e) => onNameChange(e.target.value)}
                        maxLength={50}
                        error={nameError || undefined}
                        style={{ flex: 1 }}
                    />
                    <Button
                        size="xs"
                        loading={isSaving}
                        onClick={onSave}
                        disabled={saveButtonDisabled}
                        mt={1}
                    >
                        {isNewInstruction ? 'Save' : 'Update'}
                    </Button>
                </Group>
            </Box>
        );
    }

    return (
        <Box mb="xs">
            <Select
                size="xs"
                placeholder={isLoading ? 'Loading…' : 'Select or create instruction'}
                data={selectData}
                value={selectedId}
                onChange={onSelectChange}
                disabled={isLoading}
                rightSection={isLoading ? <Loader size="xs" /> : undefined}
                clearable
                comboboxProps={{
                    portalProps: { target: PORTAL_TARGET },
                    zIndex: 1001,
                }}
                renderOption={({ option }) => (
                    <Group
                        justify="space-between"
                        style={{ width: '100%' }}
                        wrap="nowrap"
                    >
                        <Text
                            size="xs"
                            truncate="end"
                            style={{ flex: 1 }}
                            c={option.value === NEW_INSTRUCTION_VALUE ? 'brand.6' : undefined}
                            fw={option.value === NEW_INSTRUCTION_VALUE ? 600 : undefined}
                        >
                            {option.label}
                        </Text>
                        {option.value !== NEW_INSTRUCTION_VALUE && (
                            <ActionIcon
                                size="xs"
                                variant="subtle"
                                color="red"
                                aria-label={`Delete ${option.label}`}
                                onClick={(e) => onDelete(option.value, e)}
                            >
                                <IconTrash size={12} />
                            </ActionIcon>
                        )}
                    </Group>
                )}
            />

            {(isNewInstruction || isExistingInstruction) && (
                <Group gap="xs" mt="xs" align="flex-start">
                    <TextInput
                        size="xs"
                        placeholder="Instruction name (max 50 chars)"
                        value={instructionName}
                        onChange={(e) => onNameChange(e.target.value)}
                        maxLength={50}
                        error={nameError || undefined}
                        style={{ flex: 1 }}
                    />
                    <Button
                        size="xs"
                        loading={isSaving}
                        onClick={onSave}
                        disabled={saveButtonDisabled}
                        mt={1}
                    >
                        {isNewInstruction ? 'Save' : 'Update'}
                    </Button>
                </Group>
            )}
        </Box>
    );
};

export { NEW_INSTRUCTION_VALUE };
export default InstructionSelector;
